import { useEffect, type FC } from 'react'

import { Banner, BannerDescription } from '@wartek-id/banner'

import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import CardPanduan from 'app/KinerjaDinasPage/components/CardPanduan'
import CardKinerjaSteps from 'app/KinerjaShared/components/CardKinerjaSteps'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useToggleSdkContext from 'utils/useToogleSdk'
import { useKinerjaPsTrackEvent } from 'app/KinerjaPSPage/hooks/useKinerjaPsTrackEvent'
import { BannerGantiPeriode } from 'app/KinerjaShared/components/BannerGantiPeriode'
import BlockCard from 'app/KinerjaDinasPage/components/BlockCard'
import usePenetapanGuard from 'app/KinerjaDinasPage/hooks/usePenetapanGuard'
import CardPenetapanTahunan from 'app/KinerjaDinasPage/components/CardPenetapanTahunan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import useRiwayatTahunanAccessibility from 'app/KinerjaShared/hooks/useRiwayatTahunanAccessibility'
import { useApiGetPenilaianPeriodikTypePS } from 'api/kinerja/pengawas-sekolah/penilaian-periodik'
import CardPenilaianPeriodik from 'app/KinerjaShared/components/CardPenilaianPeriodik'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import usePenilaianPeriodikTrackEvent from 'app/KinerjaShared/hooks/usePenilaianPeriodikTrackEvent'

type KinerjaDinasIndexProps = {}

const KinerjaDinasIndex: FC<KinerjaDinasIndexProps> = ({}) => {
  const toggleValue = useToggleSdkContext()
  const { isKepalaDinasAndPengawasSekolah, isKepalaDinas } = useRoleContext()
  const { periodYear, periodNo } = usePeriodContext()
  const { trackEvent } = usePenilaianPeriodikTrackEvent()

  const { trackOpenKinerjaPenilaianHomepage } = useKinerjaPsTrackEvent()

  const { isEnabled: isPenetapanEnabled } = usePenetapanGuard()

  // Used for checking if the current period is within allowed range, eg. current context is 2024, then check for 2024
  const { isWithinAllowedRange } = useRiwayatTahunanAccessibility({})

  // Used for checking if the previous period is within allowed range, eg. current context is 2024, then check for 2023
  const {
    isSuccess: isPrevSuccessGetCommonConfig2,
    isWithinAllowedRange: isPrevWithinAllowedRange,
  } = useRiwayatTahunanAccessibility({ contextYear: periodYear - 1 })

  useEffect(() => {
    trackOpenKinerjaPenilaianHomepage('KINERJA_KS_PENILAI_DINAS_HOMEPAGE')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isPenilaianPeriodikEnabled =
    toggleValue?.['kinerja-penilaian-periodik-enabled'] && periodYear >= 2025
  const { data: periodicTypeData, isSuccess: isSuccessPeriodicType } =
    useApiGetPenilaianPeriodikTypePS({
      params: { skpYear: periodYear },
      options: {
        enabled: isPenilaianPeriodikEnabled,
        staleTime: 60 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
    })

  // ------------------
  // Penilaian periodik
  // ------------------
  const handleClickPenilaianPeriodikEntrypoint = () => {
    trackEvent({
      activity_type: 'open_homepage',
      screen_name: 'penilaian_periodik',
      attributes: {
        year: periodYear,
        role: 'dinas',
      },
    })
  }

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu="kadin-ks" />

      <div className="p-6 flex flex-col gap-6 w-full">
        <BannerGantiPeriode />

        {isPrevSuccessGetCommonConfig2 && isPrevWithinAllowedRange && (
          <Banner
            title={`Lakukan Penetapan Predikat Kinerja (Tahunan - ${
              periodYear - 1
            })`}
            type="informational"
            closable={false}
          >
            <BannerDescription className="!text-base !text-default">
              Penetapan sangat dibutuhkan oleh pegawai Anda. Mohon ganti periode
              ke tahun sebelumnya.
            </BannerDescription>
          </Banner>
        )}

        <CardPanduan />
        <BlockCard
          showPantauPerencanaan={
            toggleValue?.['kinerja-dinas-perencanaan-enabled']
          }
          showPantauPelaksanaan
          showPenilaian={isKepalaDinasAndPengawasSekolah}
          showPenetapan={isKepalaDinas && isPenetapanEnabled}
        />

        {/* Place on bottom if penetapan tahunan is actionable */}
        {isWithinAllowedRange && periodNo === 2 && (
          <CardPenetapanTahunan key={periodYear} />
        )}

        {isSuccessPeriodicType && (
          <CardPenilaianPeriodik
            periodicType={periodicTypeData.data.periodicType}
            href={`${PENGELOLAAN_KINERJA_KADIN_PATH}/periodik`}
            onCtaClick={handleClickPenilaianPeriodikEntrypoint}
          />
        )}

        <CardKinerjaSteps />
      </div>
    </div>
  )
}
export default KinerjaDinasIndex
